import "./CreateAd.scss";

import * as formik from 'formik';
import * as yup from 'yup';

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AdAccountSection from "./components/AdAccountSection";
import AdPreviewModal from "./components/AdPreviewModal";
import AdSummary from "./components/AdSummary";
import AppButton from "../../../commons/buttons/AppButton";
import AppLoading from "../../../commons/AppLoading";
import BudgetSection from "./components/BudgetSection";
import CompanyAPI from "../../company/CompanyAPI";
import CreativeSection from "./components/CreativeSection";
import FloatingVideoButton from "../../../commons/buttons/FloatingVideoButton";
import { Form } from 'react-bootstrap';
import HallAppCenterContainer from "../../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../../commons/HallAppToast";
import LocationSection from "./components/LocationSection";
import MetaAPI from "../MetaAPI";
import NoAdAccountFound from "./components/NoAdAccountFound";

const metaAPI = new MetaAPI();
const companyAPI = new CompanyAPI();

const { Formik } = formik;

// 🎭 MOCK DATA
const MOCK_AD_ACCOUNTS = [
    {
        id: 'act_123456789',
        name: 'Salão Beleza Total - Anúncios',
        currency: 'BRL',
        account_status: 1
    },
    {
        id: 'act_987654321',
        name: 'HallApp Marketing',
        currency: 'BRL',
        account_status: 1
    },
    {
        id: 'act_555666777',
        name: 'Estética & Cia',
        currency: 'BRL',
        account_status: 1
    }
];

const MOCK_PAGES = [
    {
        id: '100123456789',
        name: 'Salão Beleza Total',
        category: 'Beauty Salon',
        followers_count: 1250
    },
    {
        id: '100987654321',
        name: 'Espaço Hair & Beauty',
        category: 'Beauty Salon',
        followers_count: 890
    },
    {
        id: '100555666777',
        name: 'Studio de Beleza Premium',
        category: 'Beauty Salon',
        followers_count: 2340
    }
];

const USE_MOCK = false; // 🔧 Altere para false quando o backend estiver pronto

const schema = yup.object().shape({
    adAccountId: yup.string().required('Selecione a conta de anúncios'),
    pageId: yup.string().required('Selecione a página'),
    objective: yup.string().required('Selecione o objetivo'),
    dailyBudget: yup.number().min(100, 'Orçamento mínimo: R$ 1,00').required('Informe o orçamento diário'),
    startDate: yup.string().required('Informe a data de início'), // ✅ Mudado de date() para string()
    endDate: yup.string().required('Informe a data de término')
        .test('is-after-start', 'Data de término deve ser após o início', function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            return new Date(value) > new Date(startDate);
        }),
    targetGender: yup.string(),
    destinationUrl: yup.string().url('URL inválida').required('Informe o link de destino'),
    locations: yup.array().min(1, 'Selecione pelo menos uma localização'),
    primaryText: yup.string().required('Informe o texto principal').max(500, 'Máximo 500 caracteres'),
    headline: yup.string().required('Informe o título').max(100, 'Máximo 100 caracteres'),
    ageMin: yup.number().min(13, 'Idade mínima: 13').max(65, 'Idade máxima: 65').required('Informe a idade mínima'),
    ageMax: yup.number().min(13, 'Idade mínima: 13').max(65, 'Idade máxima: 65').required('Informe a idade máxima')
        .test('is-greater', 'Idade máxima deve ser maior que a mínima', function (value) {
            const { ageMin } = this.parent;
            if (!ageMin || !value) return true;
            return value >= ageMin;
        }),
});

const STEPS = [
    { id: 1, title: 'Conta e Página', description: 'Selecione onde anunciar' },
    { id: 2, title: 'Orçamento', description: 'Configure valores e público' },
    { id: 3, title: 'Localização', description: 'Defina áreas de exibição' }, // ✅ novo
    { id: 4, title: 'Criativo', description: 'Textos e imagem do anúncio' },
    { id: 5, title: 'Revisão', description: 'Confirme os dados' },
];

function CreateAd() {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const [adAccounts, setAdAccounts] = useState([]);
    const [pages, setPages] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isEdit, setEdit] = useState(false);


    const [initialValues, setInitialValue] = useState({
        adAccountId: '',
        pageId: '',
        objective: 'LINK_CLICKS',
        dailyBudget: 50,
        startDate: '',
        endDate: '',
        destinationUrl: 'https://hallapp.com.br',
        primaryText: 'Conheça a HallApp e simplifique a gestão do seu salão!',
        headline: 'Agende com facilidade',
        ageMin: 18,
        ageMax: 65,
        targetGender: 'all',
        interests: [],
        // Localização
        locations: [],            // [{type:'city'|'address'|'region'|'country', key:'São Paulo, BR', radius:10, unit:'kilometer', geoId:'123'}]
        excludeLocations: [],     // mesmo formato
        locationAdvantage: false,  // público advantage de localização
    });

    const { state } = useLocation();
    const ad = state?.ad;


    useEffect(() => {
        setEdit(ad != null && ad != undefined)
        loadMetaData();
    }, []);

    const loadMetaData = async () => {
        try {
            setIsLoading(true);

            if (USE_MOCK) {
                // 🎭 Simula delay de API
                await new Promise(resolve => setTimeout(resolve, 1000));

                setAdAccounts(MOCK_AD_ACCOUNTS);
                setPages(MOCK_PAGES);

                console.log('📦 Usando dados MOCK');
            } else {
                const [accResponse, pageResponse] = await Promise.all([
                    metaAPI.getAdAccounts(),
                    metaAPI.getPages()
                ]);

                setAdAccounts(accResponse?.data || []);
                setPages(pageResponse?.data || []);


            }

        } catch (error) {
            console.error('Erro ao carregar dados da Meta:', error);
            HallAppToast.error('Não foi possível carregar os dados da Meta');
        } finally {
            setIsLoading(false);
            if (ad != null && ad != undefined) loadEdit()
        }
    };

    const loadEdit = () => {
        setInitialValue({
            adAccountId: ad?.adAccountId,
            pageId: ad?.targeting?.pageId,
            objective: ad?.objective,
            dailyBudget: ad?.dailyBudget,
            startDate: ad?.startTime,
            endDate: ad?.endTime,
            ageMin: ad?.targeting?.ageMin,
            ageMax: ad?.targeting?.ageMax,
            targetGender: ad?.targeting?.gender,
            locations: ad?.targeting?.locations,
            interests: ad?.targeting?.interests,
            primaryText: ad?.targeting?.creative?.message,
            headline: ad?.targeting?.creative?.name,
            destinationUrl: ad?.targeting?.creative?.link,
            imageUrl: ad?.targeting?.creative?.imageLink,
            adSetId: ad?.targeting?.adsetId,
            internalId: ad?.internalId,
        })
    }

    const validateStep = (values, errors) => {
        switch (currentStep) {
            case 1:
                return !errors.adAccountId && !errors.pageId &&
                    values.adAccountId && values.pageId;

            case 2:
                const hasStep2Values = values.objective &&
                    values.dailyBudget &&
                    values.startDate &&
                    values.endDate &&
                    values.ageMin &&
                    values.ageMax;

                const hasStep2Errors = !errors.objective ||
                    !errors.dailyBudget ||
                    !errors.startDate ||
                    !errors.endDate ||
                    !errors.ageMin ||
                    !errors.ageMax;

                return hasStep2Values && hasStep2Errors;

            case 3:
                const hasLocations = Array.isArray(values.locations) && values.locations.length > 0;
                const hasInterests = Array.isArray(values.interests) && values.interests.length > 0;
                return hasLocations && hasInterests;


            case 4:
                return !errors.primaryText &&
                    !errors.headline &&
                    !errors.destinationUrl &&
                    values.primaryText &&
                    values.headline &&
                    values.destinationUrl &&
                    (imageFile || values.imageUrl);

            default:
                return true;
        }
    };

    const handleNext = (values, errors) => {
        if (!validateStep(values, errors)) {
            if (currentStep === 4 && !imageFile) {
                HallAppToast.error('Selecione uma imagem para o anúncio');
            } else {
                HallAppToast.error('Preencha todos os campos obrigatórios');
            }
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    };

    const handlePrevious = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };


    const createOrUpdate = async (values) => {
        if (!imageFile && !values.imageUrl) {
            HallAppToast.error('Selecione uma imagem para o anúncio');
            return;
        }

        try {
            setIsSaving(true);

            if (USE_MOCK) {
                // 🎭 Simula criação de anúncio
                await new Promise(resolve => setTimeout(resolve, 2000));

                console.log('📦 MOCK - Anúncio criado:', {
                    ...values,
                    imageFile: imageFile.name,
                    companyId: companyAPI.getCompanyManagment()?.id
                });

                HallAppToast.success('Anúncio criado com sucesso! (MOCK)');

                // Simula navegação
                // setTimeout(() => {
                //     navigate('/marketing/conectar');
                // }, 1500);

                return;
            }

            const formData = new FormData();
            const data = {
                adSetId: values.adSetId,
                campaignId: ad?.targeting?.campaignId,
                creativeId: ad?.targeting?.creativeId,
                companyId: companyAPI.getCompanyManagment().id,
                adAccountId: values.adAccountId,
                pageId: values.pageId,
                objective: values.objective,
                dailyBudget: values.dailyBudget,
                startDate: values.startDate,
                endDate: values.endDate,
                destinationUrl: values.destinationUrl,
                primaryText: values.primaryText,
                headline: values.headline,
                locationTarget: values.locationTarget,
                ageMin: values.ageMin,
                ageMax: values.ageMax,
                targetGender: values.targetGender,
                locations: values.locations || [],
                excludeLocations: values.excludeLocations,
                locationAdvantage: values.locationAdvantage,
                interests: values.interests || [],
                internalId: ad?.internalId
            };

            formData.append('dto', new Blob([JSON.stringify(data)], { type: 'application/json' }));
            formData.append('image', imageFile);

            if (isEdit) {
                await metaAPI.updateAd(formData);
            } else {
                await metaAPI.createAd(formData);
            }

            HallAppToast.success('Anúncio criado com sucesso!');
            navigate('/marketing/meus-anuncios');

        } catch (error) {
            console.error('Erro ao criar anúncio:', error);
            HallAppToast.error(error?.response?.data?.message || 'Erro ao criar anúncio');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="create-ad-loading">
                <HallAppCenterContainer>
                    <AppLoading />
                    <p style={{ marginTop: '20px', textAlign: 'center' }}>
                        {USE_MOCK ? 'Carregando dados MOCK...' : 'Carregando dados da Meta...'}
                    </p>
                </HallAppCenterContainer>
            </div>
        );
    }

    return (
        <div className="create-ad-page">
            {USE_MOCK && (
                <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    maxWidth: '800px',
                    margin: '0 auto 20px'
                }}>
                    <strong>🎭 Modo MOCK ativado</strong> - Usando dados de teste
                </div>
            )}

            <div className="create-ad-header">
                <h2 className="create-ad-title">
                    {isEdit ? 'Editar Anúncio' : 'Criar Anúncio'}
                </h2>
                <p className="create-ad-subtitle">
                    Configure sua campanha de anúncios no Facebook e Instagram
                </p>
            </div>
            <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://www.youtube.com/embed/Css4jMHo5J0"} />
            {/* Progress Steps */}
            <div className="create-ad-steps">
                {STEPS.map((step, index) => (
                    <div
                        key={step.id}
                        className={`step-item ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                    >
                        <div className="step-number">{step.id}</div>
                        <div className="step-info">
                            <div className="step-title">{step.title}</div>
                            <div className="step-description">{step.description}</div>
                        </div>
                        {index < STEPS.length - 1 && <div className="step-line"></div>}
                    </div>
                ))}
            </div>

            {
                adAccounts.length === 0 ?
                    <NoAdAccountFound onRefresh={loadMetaData} /> :
                    <Formik
                        validationSchema={schema}
                        onSubmit={createOrUpdate}
                        initialValues={initialValues}
                        enableReinitialize={true}
                    >
                        {({ handleSubmit, handleChange, values, touched, errors, setFieldValue, validateForm }) => (
                            <Form noValidate onSubmit={handleSubmit}>

                                {/* Step Content */}
                                <div className="create-ad-content">
                                    {currentStep === 1 && (
                                        <AdAccountSection
                                            isEdit={isEdit}
                                            adAccounts={adAccounts}
                                            pages={pages}
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                        />
                                    )}

                                    {currentStep === 2 && (
                                        <BudgetSection
                                            isEdit={isEdit}
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            handleChange={handleChange}
                                            setFieldValue={setFieldValue}
                                        />
                                    )}

                                    {currentStep === 3 && (
                                        <LocationSection
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                        />
                                    )}

                                    {currentStep === 4 && (
                                        <CreativeSection
                                            isEdit={isEdit}
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            handleChange={handleChange}
                                            imageFile={imageFile}
                                            setImageFile={setImageFile}
                                            setFieldValue={setFieldValue}
                                        />
                                    )}

                                    {currentStep === 5 && (
                                        <AdSummary
                                            values={values}
                                            imageFile={imageFile}
                                            adAccounts={adAccounts}
                                            pages={pages}
                                        />
                                    )}
                                </div>

                                {/* Navigation Buttons */}
                                <div className="create-ad-actions">
                                    <div className="actions-left">
                                        <AppButton
                                            type="button"
                                            name="Cancelar"
                                            variant="secondary"
                                            onClick={() => navigate('/marketing/conectar')}
                                            disabled={isSaving}
                                        />
                                    </div>

                                    <div className="actions-right create-ad-navigation">
                                        {currentStep > 1 && (
                                            <AppButton
                                                type="button"
                                                name="Voltar"
                                                variant="secondary"
                                                onClick={handlePrevious}
                                                disabled={isSaving}
                                            />
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => setShowPreview(true)}
                                            className="nav-button preview-button"
                                            style={{
                                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                width: '100%',
                                                cursor: 'pointer',
                                                fontSize: '15px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            Ver Anúncio
                                        </button>

                                        {currentStep < STEPS.length ? (
                                            <AppButton
                                                type="button"
                                                name="Próximo"
                                                onClick={async () => {
                                                    const validationErrors = await validateForm();
                                                    handleNext(values, validationErrors);
                                                }}
                                                disabled={isSaving}
                                            />
                                        ) : (
                                            <>
                                                {isSaving ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <AppLoading />
                                                        <span>Criando anúncio...</span>
                                                    </div>
                                                ) : (
                                                    <AppButton
                                                        type="button"
                                                        name={isEdit ? "Editar Anúncio" : "Criar Anúncio"}
                                                        onClick={() => {
                                                            createOrUpdate(values);
                                                        }}
                                                        disabled={isSaving}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <AdPreviewModal
                                    show={showPreview}
                                    closeModal={() => setShowPreview(false)}
                                    adData={values}
                                    imageFile={imageFile}
                                />
                            </Form>
                        )}

                    </Formik>
            }
     

        </div>
    );
}

export default CreateAd;